import type { ReactNode } from "react";
import { ToWords } from "to-words/en-IN";
import AuthlessPrintChrome from "@/components/common-receipt/AuthlessPrintChrome";
import { useAuthlessPrintRow } from "@/components/common-receipt/use-authless-print-row";
import ReceiptPrint from "@/components/receipt-layout";
import { useHardcopyBorders } from "@/theme/useHardcopyBorders";
import { formatDisplayDateTime } from "@/utils/date-format";

type FmbPrintRow = Record<string, unknown> & {
  fmbData?: Record<string, unknown> & { itsdata?: Record<string, unknown> };
  allocations?: {
    fmbTakhmeen?: Record<string, unknown>;
    fmbContribution?: Record<string, unknown>;
  }[];
};

function LabelValue({
  label,
  value,
  noBorder,
  borderBottom,
}: {
  label: string;
  value: ReactNode;
  noBorder?: boolean;
  borderBottom: string;
}) {
  return (
    <div style={{ borderBottom: noBorder ? undefined : borderBottom }}>
      <div style={{ textAlign: "right", padding: "10px", fontSize: "14px" }}>{label}</div>
      <div style={{ textAlign: "right", padding: "10px" }}>{value}</div>
    </div>
  );
}

const FmbReceipt = () => {
  const { solid1, solid1Soft, solid5 } = useHardcopyBorders();
  const { data, error } = useAuthlessPrintRow<FmbPrintRow>({
    location: "fmbReceipt",
    idFromSearchParam: "receiptId",
    buildId: (id) => `print/${id}`,
  });

  return (
    <AuthlessPrintChrome data={data} error={error}>
      {(receiptData) => {
        const fmbData = (receiptData.fmbData ?? {}) as FmbPrintRow["fmbData"];
        const itsdata = (fmbData?.itsdata ?? {}) as Record<string, unknown>;
        const allocations = Array.isArray(receiptData.allocations) ? receiptData.allocations : [];
        const firstAnnual = allocations.find((a) => a?.fmbTakhmeen);
        const firstContrib = allocations.find((a) => a?.fmbContribution);
        const fmbTakhmeen = (firstAnnual?.fmbTakhmeen ?? {}) as Record<string, unknown>;
        const fmbContribution = (firstContrib?.fmbContribution ?? {}) as Record<string, unknown>;
        const hijriStart = (fmbTakhmeen.hijriYearStart ??
          fmbContribution.hijriYearStart ??
          null) as number | null;
        const hijriEnd = (fmbTakhmeen.hijriYearEnd ?? (hijriStart ? hijriStart + 1 : null)) as
          | number
          | null;
        const fmbPeriodLabel = hijriStart && hijriEnd ? `${hijriStart}-${hijriEnd}` : "—";

        const toWords = new ToWords();
        const amountNum = Number(receiptData.amount ?? 0);

        return (
          <ReceiptPrint subTitle="FMB Receipt">
            <div style={{ display: "flex", width: "100%" }}>
              <div
                className="u-col u-col-82p27"
                style={{ boxSizing: "border-box", padding: "0", borderTop: solid5 }}
              >
                <div style={{ padding: "20px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div style={{ paddingRight: "10px" }}>نام</div>
                    <div style={{ flex: "3", borderBottom: solid1Soft }}>
                      {String(itsdata.Full_Name ?? "")}
                    </div>
                    <div style={{ paddingLeft: "10px" }}>حفظ الله تعالى</div>
                  </div>
                </div>
                <div style={{ padding: "10px", borderBottom: solid1 }}>
                  {String(itsdata.Address ?? "")}, {String(itsdata.City ?? "")} -{" "}
                  {String(itsdata.Pincode ?? "")}, {String(itsdata.State ?? "")}
                </div>
                <div style={{ textAlign: "center", padding: "10px" }}>بعد السلام الجميل</div>
                <div style={{ padding: "10px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div style={{ flex: "3", paddingRight: "10px", borderBottom: solid1Soft }}>
                      {toWords.convert(amountNum)} Only
                    </div>
                    <div style={{ flex: "0.5", paddingRight: "10px", textAlign: "center" }}>
                      انكه
                    </div>
                    <div style={{ flex: "1", borderBottom: solid1Soft }}>
                      {String(receiptData.amount ?? "")}
                    </div>
                    <div style={{ flex: "2", paddingLeft: "10px", textAlign: "right" }}>
                      اٰپ طرف سي روپية
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "center", padding: "10px" }}>
                  <strong>فيض المواىٔد البره‍انية</strong>&ldquo; فند ما&rdquo;
                </div>

                <div style={{ padding: "10px" }}>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <div style={{ paddingRight: "10px" }}>ما وصول تهيا چهے</div>
                    <div style={{ borderBottom: solid1Soft }}>{fmbPeriodLabel}</div>
                  </div>
                </div>
              </div>

              <div
                className="u-col u-col-17p73"
                style={{
                  boxSizing: "border-box",
                  borderTop: solid5,
                  borderLeft: solid5,
                }}
              >
                <LabelValue
                  borderBottom={solid1}
                  label="تاريخ"
                  value={formatDisplayDateTime(receiptData.createdAt as string)}
                />
                <LabelValue
                  borderBottom={solid1}
                  label="رسيد نمبر"
                  value={String(receiptData.receiptNo ?? "")}
                />
                <LabelValue
                  borderBottom={solid1}
                  label="HOF ITS"
                  value={String(itsdata.ITS_ID ?? "")}
                  noBorder
                />
              </div>
            </div>
          </ReceiptPrint>
        );
      }}
    </AuthlessPrintChrome>
  );
};

export default FmbReceipt;
